<template>
  <div>
    <div>
      <div class="d-flex" style="margin-bottom: 1.70rem;">
        <router-link :to="`/${this.currentProduct.toLowerCase()}/legal-document-template`">
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="38" viewBox="0 0 40 38" fill="none" style="cursor: pointer;">
            <path
              d="M38.75 19.0003H1.25M1.25 19.0003L18.9583 1.29199M1.25 19.0003L18.9583 36.7087"
              stroke="#FF7C48"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </router-link>
        <div class="d-flex align-items-center" style="margin-left: 1.25rem; gap: 2.34rem;">
          <div>
            <h1 class="title">{{ $t("legal-document-template.plural") }}</h1>
          </div>
        </div>
      </div>

      <h2 v-if="!isLoading && editedEntity" class="mb-4">
        <span v-if="isEditMode">"{{ editedEntity.name | translate }}" {{ $t("common.update") }}</span>
        <span v-else>{{ $t("legal-document-template-category.new") }}</span>
      </h2>

      <b-row>
        <b-col>
          <h6 class="mt-2">{{ $t("common.language") }}</h6>
          <multiselect
            v-model="form.language"
            track-by="language"
            :placeholder="$t('common.language-select')"
            :options="languages"
            :searchable="false"
            :allow-empty="false"
            :show-labels="false"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <img :src="`${option.flag}`" alt="user-image" class="mr-1" height="12" />
              <span class="align-middle">{{ option.title[option.language] }}</span>
            </template>
            <template slot="option" slot-scope="{ option }">
              <img :src="`${option.flag}`" alt="user-image" class="mr-1" height="12" />
              <span class="align-middle">{{ option.title[option.language] }}</span>
            </template>
          </multiselect>

          <sweet-input
            class="mt-3"
            v-model="form.name"
            :isValid="!$v.form.name.$error"
            @input="$v.form.name.$touch()"
            layout="vertical"
            :placeholder="$t('legal-document-template.name')"
            :label="$t('legal-document-template.name')"
          />

          <h6 class="mt-3">{{ $t("legal-document-template-category.text") }} :</h6>
          <div class="mb-3">
            <filter-select
              id="categoryFilter"
              ref="categoryFilter"
              :placeholder="$t('legal-document-template-category.text')"
              :multiple="false"
              @filter="onCategorySearch"
              :selected="form.category"
              @changed="onCategorySelect"
            />
          </div>

          <sweet-textarea
            class="mt-3"
            v-model="form.description"
            :isValid="!$v.form.description.$error"
            @input="$v.form.description.$touch()"
            type="text"
            layout="vertical"
            :placeholder="$t('common.description')"
            :label="$t('common.description')"
            :rows="5"
          />
        </b-col>
        <b-col offset="1">
          <LegalDocumentTemplateFilter id="legal-document-filter" :multipleFields="editedEntity.requiredCriterias" @apply="onFilterApplied" />

          <b-form-checkbox v-model="form.renderSingleDocument" switch class="mb-2 mt-2"> {{ $t("legal-document-template.single-document-question") }}</b-form-checkbox>
          <hr />
          <h5 class="mt-3">{{ $t("legal-document-template.filters.title") }} :</h5>

          <ul style="list-style: none;margin:0px;padding:0px">
            <li v-for="criteria in normalizeFilterNames().required" :key="criteria.text" class="mb-2">
              <i v-if="criteria.provided" class="bx bx-check-circle text-success"></i>
              <i v-else class="bx bx-x-circle text-danger"></i>
              <b> {{ criteria.text }}</b>

              <ol v-if="criteria.value">
                <li v-for="(v, i) in criteria.value" :key="i + v">{{ v | translate }}</li>
              </ol>
            </li>
          </ul>

          <div v-if="normalizeFilterNames().others.length > 0">
            <h5 class="mt-3">{{ $t("legal-document-template.filters.other-title") }} :</h5>

            <ul style="list-style: none;margin:0px;padding:0px">
              <li v-for="criteria in normalizeFilterNames().others" :key="criteria.text" class="mb-2">
                <b> {{ criteria.text }}</b>

                <ol v-if="criteria.value">
                  <li v-for="(v, i) in criteria.value" :key="i + v">{{ v | translate }}</li>
                </ol>
              </li>
            </ul>
          </div>

          <h6 class="mt-3">{{ $t("legal-document-template.filters.is-provided") }}</h6>
          <span :class="isRequiredCriteriasProvided() ? 'text-success' : 'text-muted'">{{ isRequiredCriteriasProvided() ? $t("common.yes") : $t("common.no") }}</span>
        </b-col>
      </b-row>

      <sweet-button @click="save()" type="submit" :loading="isUpdating" :disabled="isUpdating || $v.$invalid" variant="success" icon="mdi mdi-check" buttonClass="mt-2">
        {{ $t("common.save") }}
      </sweet-button>
      <hr />

      <h3 class="text-center m-5">{{ $t("legal-document-template.text") }}</h3>
      <Editor ref="editor" />
    </div>
  </div>
</template>

<script>
import Editor from "@c/document-editor/document-editor";
import SweetTextarea from "@c/form/sweet-textarea";
import LibraryItems from "@cc/library-items";
import { maxLength, minLength, required } from "vuelidate/lib/validators";
import LegalDocumentTemplateFilter from "./component/legal-documen-template-filter";

export default {
  components: { LibraryItems, LegalDocumentTemplateFilter, Editor, SweetTextarea },
  data() {
    return {
      form: {
        description: undefined,
        category: undefined,
        language: undefined,
        product: "GDPR",
        filters: {},
        content: [],
      },
      filterNameMap: {
        organization: this.$t("legal-document-template.filters.organization"),
        department: this.$t("legal-document-template.filters.department"),
        process: this.$t("legal-document-template.filters.process"),
        datasource: this.$t("legal-document-template.filters.datasource"),
        dataCategory: this.$t("legal-document-template.filters.data-category"),
        data: this.$t("legal-document-template.filters.data"),
        datasubject: this.$t("legal-document-template.filters.datasubject"),
        directCollectMethod: this.$t("legal-document-template.filters.direct-collection-method"),
        otherCollectMethod: this.$t("legal-document-template.filters.other-collection-method"),
        electronicStorage: this.$t("legal-document-template.filters.electronic-storage"),
        physicalStorage: this.$t("legal-document-template.filters.physical-storage"),
        transferingPurpose: this.$t("legal-document-template.filters.transfering-purpose"),
        transferingLegalReason: this.$t("legal-document-template.filters.transfering-legal-reason"),
        transferingMethod: this.$t("legal-document-template.filters.transfering-method"),
        receiverGroup: this.$t("legal-document-template.filters.receiver-group"),
        thirdParty: this.$t("legal-document-template.filters.third-party"),
        // transferingDirection: this.$t("legal-document-template.filters.transfering-direction"),
        // transferingAbroad: this.$t("legal-document-template.filters.transfering-abroad"),
      },
    };
  },
  validations: {
    form: {
      name: {
        required,
        minLength: minLength(1),
        maxLength: maxLength(350),
      },
      description: {
        required,
        minLength: minLength(1),
        maxLength: maxLength(4000),
      },
      category: {
        required,
      },
      language: {
        required,
      },
    },
  },
  computed: {
    criteria() {
      return this.$store.getters["legalDocumentFilter/criteria"];
    },
    editedEntity() {
      return this.$store.getters[`organizationLegalDocumentTemplate/editedEntity`];
    },

    isLoading() {
      return this.$store.getters[`organizationLegalDocumentTemplate/isLoading`];
    },
    isUpdating() {
      return this.$store.getters[`organizationLegalDocumentTemplate/isUpdating`];
    },
    isEditMode() {
      return true;
    },

    languages() {
      return this.$store.getters["localization/languages"];
    },
  },
  watch: {},
  mounted() {
    this.init();
  },
  methods: {
    isRequiredCriteriasProvided() {
      const criterias = { ...this.criteria };

      return this.editedEntity?.requiredCriterias?.filter((i) => i != "organization").every((i) => criterias[i]?.length > 0);
    },
    normalizeFilterNames() {
      const response = { others: [], required: [] };
      if (this.editedEntity?.requiredCriterias) {
        const criterias = { ...this.criteria };

        for (const i of Object.keys(criterias)) {
          if (i == "organization") {
            continue;
          }
          if (!this.editedEntity.requiredCriterias.includes(i) && criterias[i]?.length > 0) {
            response.others.push({ text: this.filterNameMap[i], value: criterias[i].map((i) => i.name) });
          }
        }

        for (const i of this.editedEntity.requiredCriterias) {
          if (i == "organization") {
            continue;
          }

          response.required.push({ text: this.filterNameMap[i], provided: criterias[i]?.length > 0, value: criterias[i]?.map((i) => i.name) });
        }
      }
      return response;
    },
    async init() {
      try {
        const id = this.$route.params.id;
        if (id) {
          const { data } = await this.$store.dispatch(`organizationLegalDocumentTemplate/findById`, id);
          this.$store.commit(`organizationLegalDocumentTemplate/editedEntity`, data);
          this.$store.commit(`legalDocumentFilter/criteria`, { ...(data.criteria ?? {}), organization: true });

          this.form = {
            ...data,
            language: this.languages.find((i) => i.language == data.language),
            name: data.name[this.currentLanguage],
            description: data.description[this.currentLanguage],
          };

          this.$refs.editor.content = data.content;
        }
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error, { customMessage: this.$t("common.sorry-page-not-found") });

        setTimeout(() => {
          this.$router.push(`/${this.currentProduct.toLowerCase()}/legal-document-template`);
        }, 1000);
      }
    },
    async save() {
      const ok = await this.confirm(this.$t(`alert.approve`));
      if (!ok) {
        return;
      }
      try {
        const form = {
          ...this.form,
          content: this.$refs.editor.getEditorContent(),
          criteria: this.criteria,
          language: this.form.language.language,
        };
        await this.$store.dispatch(`organizationLegalDocumentTemplate/save`, form);
        this.$toast.success(this.$t("common.success"));

        setTimeout(() => {
          this.$router.push(`/${this.currentProduct.toLowerCase()}/legal-document-template`);
        }, 1000);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async onCategorySearch(value, callback) {
      const response = await this.$store.dispatch(`legalDocumentTemplateCategory/paginate`, {
        options: { limit: 200 },
        query: { name: { value } },
      });

      callback(response.data?.docs ?? []);
    },
    onCategorySelect(category) {
      this.form = { ...this.form, category };
    },

    onFilterApplied() {
      this.normalizeFilterNames();
      this.$forceUpdate();
    },
  },
};
</script>
